/**
 * Static Tags Hooks
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';

export interface StaticTag {
  id: string;
  name: string;
  color: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStaticTagDto {
  name: string;
  color: string;
}

export interface UpdateStaticTagDto {
  name?: string;
  color?: string;
}

const KEYS = {
  all: ['smswiser', 'static-tags'] as const,
};

/** Cache static tags aggressively; only refetch when user creates/updates/deletes. */
const STATIC_TAGS_STALE_MS = Infinity;
const STATIC_TAGS_GC_MS = 60 * 60 * 1000; // 1 hour

export function useStaticTags(enabled = true) {
  return useQuery({
    queryKey: KEYS.all,
    queryFn: async () => {
      const res = await api.get<{ data: StaticTag[] }>('/smswiser/static-tags');
      return res.data.data;
    },
    staleTime: STATIC_TAGS_STALE_MS,
    gcTime: STATIC_TAGS_GC_MS,
    enabled,
  });
}

export function useCreateStaticTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateStaticTagDto) => {
      const res = await api.post<{ data: StaticTag }>('/smswiser/static-tags', dto);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateStaticTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateStaticTagDto }) => {
      const res = await api.patch<{ data: StaticTag }>(`/smswiser/static-tags/${id}`, dto);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeleteStaticTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/smswiser/static-tags/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}
